import { useContext, useEffect, useRef, useState } from 'react';
import Header from './lightBoxHeader';
import Tools from './lightBoxTools';
import Diagram, { DiagramSVG } from 'Guide/Schematics/Diagram/diagram';
import Modal from '../Generic/modal';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { MinimalDisclosure } from 'Guide/Schematics/Diagram/Logic/minimalDisclosure';
import { OverlayProvider } from '../Providers/overlayContext';
import { KeypressActionProvider } from '../Providers/keypressContext';
import { DiagramHash } from '../Providers/withRouting';
import { _js } from '@ifixit/localize';
import ApiRequest from 'Shared/api_2_0';
import { WholeDiagram } from 'Guide/Schematics/Diagram/Providers/dataContext';
import includes from 'lodash/fp/includes';
import { iFixitHotSpotItem } from '../Generic/iFixitHotSpotItem';
import { ZoomContext } from 'Guide/Schematics/Diagram/Providers/d3Zoomable';
import { OpenTooltipProvider, TooltipContext } from '../Providers/tooltipContext';
import { Cluster as ClusterType } from 'Guide/Schematics/Diagram/Providers/clusteredData';
import {
   SearchContextProvider,
   useSearchContext,
} from 'Guide/Schematics/Diagram/Providers/searchContext';
import { HotSpot as HotSpotComponent } from '../Generic/hotspot';
import { Cluster } from '../Generic/cluster';

function useFetchData(diagramid: number | string, isOpen: boolean) {
   const [data, setData] = useState({
      hotspots: [],
      imageUrl: '',
      height: 0,
      width: 0,
      hasFetched: false,
   });

   useEffect(() => {
      if (!isOpen || data.hasFetched) {
         return;
      }

      new ApiRequest('diagrams/' + diagramid, {
         statusPanelMessage: _js('Loading...'),
      })
         .send()
         .then((response: WholeDiagram<iFixitHotSpotItem>) => {
            setData({
               // @ts-expect-error TS(2322) FIXME: Type 'HotSpot<iFixitHotSpotItem>[]' is not assigna... Remove this comment to see the full error message
               hotspots: response.data,
               imageUrl: response.imageUrl,
               height: response.height,
               width: response.width,
               hasFetched: true,
            });
         });
   }, [data.hasFetched, isOpen]);

   return { ...data };
}

const sanitizeText = (text: string) => text.replace(/[^\d A-Za-z]/g, '').toLowerCase();

export function filterIFixitDataPoint(searchText: string, datapoint: iFixitHotSpotItem) {
   const sanitizedSearchText = sanitizeText(searchText);

   const sanitizedDescription = sanitizeText(datapoint.description);
   const wiki = datapoint.wiki || {};
   const sanatizedValues = Object.keys(wiki).map(key =>
      sanitizeText(String(wiki[key as keyof typeof wiki]))
   );

   const someWikiMatch = sanatizedValues.some(value => includes(sanitizedSearchText, value));

   const textIncludes = includes(sanitizedSearchText, sanitizedDescription);

   return someWikiMatch || textIncludes;
}

export type LightBoxProps = {
   title: string;
   diagramid: string | number;
   initialSearchText?: string;
} & MinimalDisclosure &
   SerachableDiagramSVGProps;

const LightBox = ({
   title,
   diagramid,
   isOpen,
   onClose,
   initialSearchText,
   initialHotSpotId,
}: LightBoxProps) => {
   const diagramContainerRef = useRef<HTMLDivElement>(null);

   useEffect(() => {
      if (isOpen) {
         trackAnalyticsCustomEvent({
            eventCategory: 'Diagram',
            eventAction: `Diagram - Opened - ${title} (id: ${diagramid})`,
         });
      }
   }, [isOpen]);

   const { hotspots, imageUrl, height, width } = useFetchData(diagramid, isOpen);

   return (
      <OverlayProvider>
         <KeypressActionProvider>
            <Modal isOpen={isOpen} onClose={onClose} ref={diagramContainerRef}>
               <Diagram
                  imageSrc={imageUrl}
                  imageHeight={height}
                  imageWidth={width}
                  initialHotSpotId={initialHotSpotId}
                  containerRef={diagramContainerRef}
                  data={hotspots}
               >
                  <OpenTooltipProvider>
                     <SearchContextProvider
                        initialSearchText={initialSearchText}
                        searchDataPoint={filterIFixitDataPoint}
                     >
                        <DiagramClickClose />
                        <DiagramHash diagramid={diagramid} isOpen={isOpen} />
                        <Header title={title} onClose={onClose} />
                        <Tools />
                        <SearchableLightBoxSVG initialHotSpotId={initialHotSpotId} />
                     </SearchContextProvider>
                  </OpenTooltipProvider>
               </Diagram>
            </Modal>
         </KeypressActionProvider>
      </OverlayProvider>
   );
};

type SerachableDiagramSVGProps = {
   initialHotSpotId: string;
};

function SearchableLightBoxSVG<T extends iFixitHotSpotItem>({
   initialHotSpotId,
}: SerachableDiagramSVGProps) {
   const searchContext = useSearchContext<T>();

   const searchData = searchContext.filteredData;
   const initialHotspotIsFirstSearchResult = searchContext.searchText && searchData.length >= 1;
   const startingHotspot = initialHotspotIsFirstSearchResult
      ? searchData[0].datapointid
      : initialHotSpotId;
   const tooltipContext = useContext(TooltipContext);

   useEffect(() => {
      if (startingHotspot) {
         // Open tooltip for initial hotspot
         tooltipContext.setOpenDataPointId(startingHotspot);
      }
   }, [startingHotspot, tooltipContext.setOpenDataPointId]);

   return (
      <DiagramSVG
         initialHotSpotId={startingHotspot}
         DataPointRenderer={LightBoxDataPointRenderer}
         svgProps={{
            style: {
               height: '100%',
               width: '100%',
               position: 'fixed',
            },
         }}
         svgWrapperProps={{
            style: {
               position: 'relative',
            },
         }}
      />
   );
}

function DiagramClickClose() {
   const zoomContext = useContext(ZoomContext);
   const tooltipContext = useContext(TooltipContext);
   useEffect(() => {
      if (!zoomContext.elementRef.current) {
         return;
      }

      const closeToolTips = () => {
         tooltipContext.setOpenDataPointId(null);
      };

      zoomContext.elementRef.current.addEventListener('click', closeToolTips);

      return () => {
         zoomContext.elementRef.current?.removeEventListener('click', closeToolTips);
      };
   }, [zoomContext.elementRef.current]);

   return <></>;
}

function LightBoxDataPointRenderer({ cluster }: { cluster: ClusterType<iFixitHotSpotItem> }) {
   return cluster.length === 1 ? (
      <HotSpotComponent hotspot={cluster[0]} />
   ) : (
      <Cluster cluster={cluster} />
   );
}

export default LightBox;
